// typescript
import { useEffect, useRef } from 'react';
import type { StoreConfig } from '../services/types';

export function useMoveCaddieRootToParent({
  storeConfig,
}: {
  storeConfig?: StoreConfig;
}): void {
  const originalRef = useRef<{
    parent: Node | null;
    nextSibling: Node | null;
  } | null>(null);

  useEffect(() => {
    const targetSpec = storeConfig?.searchConfig?.superbarRootParent;
    if (!targetSpec) {
      // If config removed, try to restore
      const caddieRoot = document.querySelector(
        '#caddie-root'
      ) as HTMLElement | null;
      if (originalRef.current && caddieRoot) {
        const { parent, nextSibling } = originalRef.current;
        if (parent) {
          try {
            parent.insertBefore(caddieRoot, nextSibling);
          } catch {
            // ignore
          }
        }
        originalRef.current = null;
      }
      return;
    }

    let observer: MutationObserver | null = null;
    let timeoutId: number | null = null;
    let stopped = false;

    const resolveTarget = (): HTMLElement | null => {
      if (typeof targetSpec === 'string') {
        return document.querySelector<HTMLElement>(targetSpec);
      }
      // defensive cast for non-primitive target
      return (targetSpec as any) instanceof HTMLElement
        ? (targetSpec as HTMLElement)
        : null;
    };

    const tryMove = (): boolean => {
      const caddieRoot = document.querySelector(
        '#caddie-root'
      ) as HTMLElement | null;

      if (!caddieRoot) return false;
      const target = resolveTarget();

      if (!target) return false;

      if (caddieRoot.parentNode !== target) {
        if (!originalRef.current) {
          originalRef.current = {
            parent: caddieRoot.parentNode,
            nextSibling: caddieRoot.nextSibling,
          };
        }
        try {
          target.appendChild(caddieRoot);
        } catch {
          // ignore DOM errors
        }
      }
      return true;
    };

    // Try once immediately
    if (tryMove()) {
      return () => {
        // nothing specific to clean here (unmount restore is handled in separate effect)
      };
    }

    // Wait for either caddie-root or target to appear
    observer = new MutationObserver(() => {
      if (stopped) return;
      if (tryMove()) {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
      }
    });

    const rootToObserve = document.documentElement || document.body;
    if (rootToObserve) {
      observer.observe(rootToObserve, { childList: true, subtree: true });
      // safety timeout to stop observing after 5s
      timeoutId = window.setTimeout(() => {
        if (observer) {
          observer.disconnect();
          observer = null;
        }
        timeoutId = null;
      }, 5000);
    }

    return () => {
      stopped = true;
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };
  }, [storeConfig?.searchConfig?.superbarRootParent]);

  // Ensure restore on unmount
  useEffect(() => {
    return () => {
      const caddieRoot = document.querySelector(
        '#caddie-root'
      ) as HTMLElement | null;
      if (!caddieRoot) return;
      const saved = originalRef.current;
      if (saved && saved.parent) {
        try {
          saved.parent.insertBefore(caddieRoot, saved.nextSibling);
        } catch {
          // ignore
        }
      }
      originalRef.current = null;
    };
  }, []);
}
